import Breadcrumbs from 'Components/Breadcrumbs'
import CustomButton from 'Components/CustomButton'
import DeleteButton from 'Components/DeleteButton'
import DrawerRight from 'Components/DrawerRight'
import ListActions from 'Components/ListActions'
import PageTitle from 'Components/PageTitle'
import { WithLoader } from 'Components/WithLoader'
import VATDefinitionDetails from 'Services/ProductManagement/Features/VATDefinition/Components/VATDefinitionDetails'
import VATDefinitionList from 'Services/ProductManagement/Features/VATDefinition/Components/VATDefinitionList'
import useVATDefinitionsWithParams from 'Services/ProductManagement/Features/VATDefinition/Hooks/useVATDefinitionsWithParams'
import { VATDefinitionTabTypes } from 'Services/ProductManagement/Features/VATDefinition/types'
import {
  useDeleteSingleVatDefinitionMutation,
  useGetSingleVatDefinitionQuery
} from 'Services/ProductManagement/Features/VATDefinition/vatDefinitionApi'
import {
  PRODUCT_MANAGEMENT_ROOT_URL,
  ROWS_PER_PAGE_DEFAULT,
  SORT_ORDER_ASC,
  SORT_ORDER_DESC
} from 'Util/constants'
import { useConfirm } from 'Util/hooks/useConfirm'
import SnackbarUtils from 'Util/Snackbar'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import { TabContext, TabList, TabPanel } from '@mui/lab'
import { Box, Divider, Tab } from '@mui/material'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { t } from 'ttag'

export default function VATDefinitionListPage() {
  const baseUrl = `${PRODUCT_MANAGEMENT_ROOT_URL}/settings/vat-definitions`

  const navigate = useNavigate()
  const { confirm } = useConfirm()
  const breadcrumbs = [
    {
      title: t`Settings`
    },
    {
      title: t`VAT definitions`,
      link: baseUrl
    }
  ]

  const {
    vatDefinitions,
    isFetching,
    total_results,
    params,
    setParams,
    selectedVatDefinitionId,
    setSelectedVatDefinitionId
  } = useVATDefinitionsWithParams()

  const { data: singleVatDefinition, isFetching: isSingleVatDefinitionLoading } =
    useGetSingleVatDefinitionQuery(selectedVatDefinitionId ?? '', {
      skip: !selectedVatDefinitionId
    })
  const [deleteSingleVatDefinition, { isLoading: isDeleteLoading }] =
    useDeleteSingleVatDefinitionMutation()

  const [isDrawerOpen, setIsDrawerOpen] = useState(!!selectedVatDefinitionId)

  function handleRequestSort(_event: React.MouseEvent<unknown, MouseEvent>, property: string) {
    const isAsc = params.sort_order === SORT_ORDER_ASC
    setParams(prevState => ({
      ...prevState,
      sort_by: property,
      sort_order: `${isAsc ? SORT_ORDER_DESC : SORT_ORDER_ASC}`
    }))
  }

  const handleChangePage = (_event: unknown, newPage: number) => {
    setParams(prevState => ({
      ...prevState,
      start_index: (newPage * ROWS_PER_PAGE_DEFAULT).toString()
    }))
  }

  const handleDrawerClose = () => {
    setSelectedVatDefinitionId('')
    setIsDrawerOpen(false)
  }

  const handleRowClick = (id: string) => {
    setSelectedVatDefinitionId(id)
    setIsDrawerOpen(true)
  }

  const handleDelete = () => {
    if (!selectedVatDefinitionId) return
    deleteSingleVatDefinition({ id: selectedVatDefinitionId })
      .unwrap()
      .then(() => {
        setIsDrawerOpen(false)
        setSelectedVatDefinitionId('')
        SnackbarUtils.success(t`VAT definition successfully deleted.`)
      })
      .catch(() => {
        SnackbarUtils.error(t`An error occurred while deleting the VAT definition.`)
      })
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ mb: 2 }}>
        <Breadcrumbs items={breadcrumbs} />
        <PageTitle>{t`VAT definitions`}</PageTitle>
      </Box>
      <ListActions>
        <CustomButton
          onClick={() => navigate(`${baseUrl}/create`)}
          size="small"
          startIcon={<AddIcon />}
        >
          {t`Create new VAT definition`}
        </CustomButton>
      </ListActions>
      <Box pt={1}>
        <VATDefinitionList
          rows={vatDefinitions}
          isLoading={isFetching}
          params={params}
          handleRequestSort={handleRequestSort}
          handleChangePage={handleChangePage}
          count={total_results}
          page={
            params.start_index ? Number.parseInt(params.start_index) / ROWS_PER_PAGE_DEFAULT : 0
          }
          onRowClick={handleRowClick}
          selectedVatDefinitionId={selectedVatDefinitionId}
        />
      </Box>
      <DrawerRight
        open={isDrawerOpen}
        onClose={handleDrawerClose}
        topBar={
          <Box>
            <DeleteButton
              sx={{ mr: 1 }}
              size="small"
              onClick={() =>
                confirm({
                  title: t`Are you sure?`,
                  message: t`Are you sure you want to delete the vat definition?`,
                  handleConfirm: () => {
                    handleDelete()
                  }
                })
              }
            >{t`Delete`}</DeleteButton>
            <CustomButton
              onClick={() => navigate(`${baseUrl}/${selectedVatDefinitionId}/edit`)}
              size="small"
              startIcon={<EditIcon />}
            >{t`Edit`}</CustomButton>
          </Box>
        }
      >
        <WithLoader loading={isDeleteLoading || isSingleVatDefinitionLoading}>
          {singleVatDefinition && (
            <TabContext value={VATDefinitionTabTypes.DETAILS}>
              <TabList aria-label={t`Details`}>
                <Tab label={t`Details`} value={VATDefinitionTabTypes.DETAILS} />
              </TabList>
              <Divider />
              <TabPanel sx={{ p: 0 }} value={VATDefinitionTabTypes.DETAILS}>
                <VATDefinitionDetails vatDefinition={singleVatDefinition} />
              </TabPanel>
            </TabContext>
          )}
        </WithLoader>
      </DrawerRight>
    </Box>
  )
}
